import React from "react";
import styled from "styled-components";
import { navigate } from "gatsby";
import { BsArrowDown } from "react-icons/bs";
import Button from "../../Button";
import circles from "../../../images/home/circles.svg";
import clipboard from "../../../images/home/clipboard.svg";
import horizontalLines from "../../../images/home/horizontal-lines.svg";
import paintBucket from "../../../images/home/paint-bucket.svg";
import paintCans from "../../../images/home/paint-cans.svg";
import paintDrop from "../../../images/home/paint-drop.svg";
import plus from "../../../images/home/plus.svg";
import press from "../../../images/home/press.svg";
import swatch from "../../../images/home/swatch.svg";

const Container = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: ${({ theme }) => `calc(100vh - ${theme.navigation.height}px)`};
  height: 100%;
`;

const Inner = styled.div`
  width: 90%;
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
  min-height: ${({ theme }) => `calc(100vh - ${theme.navigation.height}px)`};

  @media (min-width: 480px) {
    width: 424px;
  }

  @media (min-width: 718px) {
    width: 680px;
  }

  @media (min-width: 1024px) {
    width: 868px;
  }

  @media (min-width: 1292px) {
    width: 1016px;
  }
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  h1 {
    text-transform: uppercase;
    font-size: 45px;
    font-family: "Oswald";
    margin-bottom: 7vh;

    span {
      &:first-of-type {
        font-size: 2rem;
        text-transform: none;
      }

      &:last-of-type {
        color: ${({ theme }) => theme.colors.white};
      }
    }
  }

  h3 {
    font-weight: 400;
    line-height: 1.8rem;

    margin-bottom: 10vh;
  }

  @media (min-width: 480px) {
    h1 {
      font-size: 50px;
    }
  }

  @media (min-width: 718px) {
    h1 {
      font-size: 60px;
    }

    h3 {
      max-width: 60%;
    }
  }

  @media (min-width: 1024px) {
    h1 {
      font-size: 70px;
    }
  }

  @media (min-width: 1292px) {
    h1 {
      font-size: 80px;
    }
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;

  svg {
    margin-top: 20px;
  }
`;

const SVG = styled.img`
  position: absolute;
  display: none;
  transform: scale(0);
  animation: scale 800ms ease-in;
  animation-fill-mode: forwards;
  animation-delay: 200ms;

  @media (min-width: 718px) {
    display: block;
  }

  @keyframes scale {
    38% {
      transform: scale(1);
    }
    55% {
      transform: scale(0.7);
    }
    72% {
      transform: scale(1);
    }
    81% {
      transform: scale(0.84);
    }
    89% {
      transform: scale(1);
    }
    95% {
      transform: scale(0.95);
    }
    100% {
      transform: scale(1);
    }
  }
`;

const Circles = styled(SVG)`
  width: 70px;
  bottom: 25px;
  right: 40%;
`;

const Clipboard = styled(SVG)`
  width: 100px;
  right: -15%;
  top: 52%;
`;

const HorizontalLines = styled(SVG)`
  width: 80px;
  top: 15%;
`;

const PaintBucket = styled(SVG)`
  width: 100px;
  bottom: 15px;
  right: -40px;
`;

const PaintCans = styled(SVG)`
  width: 100px;
  top: 48%;
  left: -100px;
`;

const PaintDrop = styled(SVG)`
  width: 100px;
  top: 70px;
  right: 20px;
`;

const Plus = styled(SVG)`
  width: 50px;
  bottom: 20%;
  left: 15%;
`;

const Press = styled(SVG)`
  width: 175px;
  bottom: 0;
  left: 0;
`;

const Swatch = styled(SVG)`
  width: 80px;
  top: 50px;
  left: 15px;
`;

export default function Banner() {
  return (
    <Container>
      <Inner>
        <Text>
          <h1>
            Custom apparel
            <br />
            <span>{`for `}</span>
            <span>students</span>
          </h1>
          <h3>
            Creating the freshest custom apparel for your organization or event,
            streamlining the ordering process, and helping you cut through the
            clutter, surfacing what matters most to you.
          </h3>
        </Text>
        <ButtonContainer>
          <Button
            onClick={() => navigate("/new-order")}
            children="Get Started"
            secondary
          />
          <BsArrowDown size={30} />
        </ButtonContainer>
        <Circles src={circles} alt="circles" />
        <Clipboard src={clipboard} alt="clipboard" />
        <HorizontalLines src={horizontalLines} alt="horizontal lines" />
        <PaintBucket src={paintBucket} alt="paint bucket" />
        <PaintCans src={paintCans} alt="paint cans" />
        <PaintDrop src={paintDrop} alt="paint drop" />
        <Plus src={plus} alt="plus sign" />
        <Press src={press} alt="printing press" />
        <Swatch src={swatch} alt="color swatch" />
      </Inner>
    </Container>
  );
}
